import type { User, UserFavourites, UserGame } from "../Data/UserContext";

/** Backend default display names before the user picks a real name (e.g. `PlayerAB123`). */
export function isAutoAssignedPlayerName(name: string | null | undefined): boolean {
    const s = typeof name === "string" ? name.trim() : "";
    if (s.length < 3) return false;
    return /^Player[A-Za-z0-9]+$/i.test(s);
}

/** True when session favourites have any non-empty value (teams, sports, grid picks, etc.). */
function hasAnyFavouriteSelection(f: UserFavourites | null | undefined): boolean {
    if (!f || typeof f !== "object") return false;
    const keys: (keyof UserFavourites)[] = [
        "favsports",
        "favcricketers",
        "faviplteams",
        "favnationalteams",
        "favfootballteams",
        "favcustomcricketteams",
    ];
    return keys.some((k) => {
        const v = f[k];
        return typeof v === "string" && v.trim() !== "";
    });
}

/**
 * Profile milestone was originally localStorage-only; infer from account data so long-time users
 * who customised before milestones existed (or saved outside the hook path) are not nagged.
 *
 * Does not treat preset `avatar_key` alone as customised (signup often sets a key while
 * `avatar_url` stays empty until a custom image). Does not treat auto `PlayerAB123`-style names
 * as a chosen display name.
 *
 * OAuth/social `original_avatar` alone does not count — users must set a profile avatar in-app
 * (`avatar_url` or milestone) or satisfy another signal below.
 */
export function inferMyZoneProfileCompleteFromSessionUser(user: User | null | undefined): boolean {
    if (!user) return false;
    const customAvatarUrl = user.avatar_url;
    const fromAvatarUrl = typeof customAvatarUrl === "string" && customAvatarUrl.trim() !== "";
    const fromFavourites = hasAnyFavouriteSelection(user.favourites);
    const pnRaw = user.player_name?.trim() ?? "";
    const fromCustomPlayerName = pnRaw !== "" && !isAutoAssignedPlayerName(pnRaw);
    return fromAvatarUrl || fromFavourites || fromCustomPlayerName;
}

export type MyZoneOnboardingMilestone = "play" | "profile" | "invite" | "spin";

export const MYZONE_ONBOARDING_MILESTONE_EVENT = "c8-myzone-onboarding-milestone";

function storageKey(userId: string | number): string {
    return `c8_myzone_ob_${String(userId)}`;
}

function readStored(userId: string | number): Partial<Record<MyZoneOnboardingMilestone, boolean>> {
    try {
        const raw = window.localStorage.getItem(storageKey(userId));
        if (!raw) return {};
        const o = JSON.parse(raw) as unknown;
        if (!o || typeof o !== "object") return {};
        return o as Partial<Record<MyZoneOnboardingMilestone, boolean>>;
    } catch {
        return {};
    }
}

function writeStored(userId: string | number, next: Partial<Record<MyZoneOnboardingMilestone, boolean>>) {
    try {
        window.localStorage.setItem(storageKey(userId), JSON.stringify(next));
    } catch {
        /* ignore */
    }
}

export function notifyMyZoneOnboardingMilestone(): void {
    try {
        window.dispatchEvent(new CustomEvent(MYZONE_ONBOARDING_MILESTONE_EVENT));
    } catch {
        /* ignore */
    }
}

/** True if the account has ever completed a wheel spin (not only today). */
export function hasEverCompletedWheelSpin(games: UserGame[] | null | undefined): boolean {
    if (!games || !Array.isArray(games)) return false;
    const entry = games.find((g) => g?.name === "wheel-spin");
    const played = entry?.values?.played;
    if (typeof played === "number" && Number.isFinite(played) && played > 0) return true;
    const n = Number(played);
    return Number.isFinite(n) && n > 0;
}

/** True when verifysession `games` shows any mini-game with a non-zero played count. */
export function hasAnyPlayedGamesFromSession(games: UserGame[] | null | undefined): boolean {
    if (!games || !Array.isArray(games)) return false;
    return games.some((g) => {
        const played = g?.values?.played;
        if (typeof played === "number" && Number.isFinite(played) && played > 0) return true;
        const n = Number(played);
        return Number.isFinite(n) && n > 0;
    });
}

export function markMyZoneOnboardingMilestone(
    userId: string | number | null | undefined,
    milestone: MyZoneOnboardingMilestone
): void {
    if (userId == null || userId === "") return;
    const uid = typeof userId === "number" ? userId : String(userId);
    const prev = readStored(uid);
    if (prev[milestone]) return;
    writeStored(uid, { ...prev, [milestone]: true });
    notifyMyZoneOnboardingMilestone();
}

/** True when a milestone is already persisted for this account (skip backfill for it). */
export function hasStoredMilestone(
    userId: string | number | null | undefined,
    milestone: MyZoneOnboardingMilestone
): boolean {
    if (userId == null || userId === "") return false;
    const uid = typeof userId === "number" ? userId : String(userId);
    return Boolean(readStored(uid)[milestone]);
}

export type EnsureOnboardingMilestonesInput = {
    userId: string | number;
    user?: User | null;
    games?: UserGame[] | null | undefined;
    hasAnyMyGamesEntry: boolean;
    myZoneFriendsLoaded?: boolean;
    myZoneFriendCount?: number;
    /** Signup first visit: do not infer profile from session until user edits profile. */
    deferProfileSessionInference?: boolean;
};

/**
 * Storage-first backfill: read localStorage, infer missing milestones from session/friends
 * data (no extra API except play history), persist so the next visit reads storage only.
 */
export function ensureOnboardingMilestonesPersisted(
    input: EnsureOnboardingMilestonesInput
): { needsPlayHistoryApi: boolean } {
    const {
        userId,
        user,
        games,
        hasAnyMyGamesEntry,
        myZoneFriendsLoaded = false,
        myZoneFriendCount = 0,
        deferProfileSessionInference = false,
    } = input;

    const uid = typeof userId === "number" ? userId : String(userId);
    let stored = readStored(uid);

    if (!stored.profile && !deferProfileSessionInference && inferMyZoneProfileCompleteFromSessionUser(user)) {
        markMyZoneOnboardingMilestone(uid, "profile");
        stored = readStored(uid);
    }

    if (!stored.spin && hasEverCompletedWheelSpin(games)) {
        markMyZoneOnboardingMilestone(uid, "spin");
        stored = readStored(uid);
    }

    if (
        !stored.invite &&
        myZoneFriendsLoaded &&
        Number.isFinite(Number(myZoneFriendCount)) &&
        Number(myZoneFriendCount) > 0
    ) {
        markMyZoneOnboardingMilestone(uid, "invite");
        stored = readStored(uid);
    }

    if (stored.play) {
        return { needsPlayHistoryApi: false };
    }

    if (hasAnyPlayedGamesFromSession(games)) {
        markMyZoneOnboardingMilestone(uid, "play");
        return { needsPlayHistoryApi: false };
    }

    if (hasAnyMyGamesEntry) {
        markMyZoneOnboardingMilestone(uid, "play");
        return { needsPlayHistoryApi: false };
    }

    return { needsPlayHistoryApi: true };
}

export function getMyZoneOnboardingCompletion(
    userId: string | number | null | undefined,
    opts: {
        hasAnyMyGamesEntry: boolean;
        games: UserGame[] | null | undefined;
        /** Current session user — used to infer profile completion without relying on localStorage. */
        user?: User | null;
        /**
         * After at least one friends API fetch for My Zone (success or failure). When false, do not
         * infer invite from friend count (avoids false “no friends” before the list loads).
         */
        myZoneFriendsLoaded?: boolean;
        /** Friends count from `/account/friends` once `myZoneFriendsLoaded` is true. */
        myZoneFriendCount?: number;
        deferProfileSessionInference?: boolean;
    }
): Record<MyZoneOnboardingMilestone, boolean> & { anyComplete: boolean } {
    const base: Record<MyZoneOnboardingMilestone, boolean> = {
        play: false,
        profile: false,
        invite: false,
        spin: false,
    };
    if (userId == null || userId === "") {
        return { ...base, anyComplete: false };
    }
    const uid = typeof userId === "number" ? userId : String(userId);
    const stored = readStored(uid);
    const play = Boolean(stored.play) || opts.hasAnyMyGamesEntry || hasAnyPlayedGamesFromSession(opts.games);
    const spin = Boolean(stored.spin) || hasEverCompletedWheelSpin(opts.games);
    const profile =
        Boolean(stored.profile) ||
        (!opts.deferProfileSessionInference && inferMyZoneProfileCompleteFromSessionUser(opts.user));
    const friendsLoaded = Boolean(opts.myZoneFriendsLoaded);
    const friendCount = Number(opts.myZoneFriendCount);
    const invite =
        Boolean(stored.invite) || (friendsLoaded && Number.isFinite(friendCount) && friendCount > 0);
    const merged = { play, profile, invite, spin };
    const anyComplete = play || profile || invite || spin;

    return { ...merged, anyComplete };
}
